import React from 'react';
import { NavLink, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, Users, CreditCard, Send, Receipt, Shield, Settings, LogOut, MessageSquare, LifeBuoy, Download, ScrollText, Bot } from 'lucide-react';
import { LogoLight } from '../components/Logo';
import { clearSession, getSession } from '../lib/api';
import { ThemeToggle } from '../lib/ThemeContext';
import { useLanguage } from '../lib/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AdminOverview from './admin/Overview';
import AdminClients from './admin/Clients';
import AdminClientDetail from './admin/ClientDetail';
import AdminCards from './admin/Cards';
import AdminTransfers from './admin/Transfers';
import AdminTransferDetail from './admin/TransferDetail';
import AdminTransactions from './admin/Transactions';
import AdminSecurity from './admin/Security';
import AdminSettings from './admin/Settings';
import AdminMessages from './admin/Messages';
import AdminTickets from './admin/Tickets';
import AdminExports from './admin/Exports';
import AdminAuditLog from './admin/AuditLog';
import AdminChatbotModeration from './admin/ChatbotModeration';

export default function DashboardAdmin() {
  const navigate = useNavigate();
  const { user } = getSession();
  const { t } = useLanguage();
  const logout = () => { clearSession(); navigate('/connexion'); };
  if (!user) return <Navigate to="/connexion" replace />;

  const items = [
    { to: '/admin', icon: LayoutDashboard, label: t('admin.nav.overview'), end: true },
    { to: '/admin/clients', icon: Users, label: t('admin.nav.clients') },
    { to: '/admin/cartes', icon: CreditCard, label: t('admin.nav.cards') },
    { to: '/admin/virements', icon: Send, label: t('admin.nav.transfers') },
    { to: '/admin/transactions', icon: Receipt, label: t('admin.nav.transactions') },
    { to: '/admin/messages', icon: MessageSquare, label: t('admin.nav.messages') },
    { to: '/admin/tickets', icon: LifeBuoy, label: t('admin.nav.support') },
    { to: '/admin/chatbot', icon: Bot, label: t('admin.nav.chatbot') },
    { to: '/admin/exports', icon: Download, label: t('admin.nav.exports') },
    { to: '/admin/audit', icon: ScrollText, label: t('admin.nav.auditLog') },
    { to: '/admin/securite', icon: Shield, label: t('admin.nav.security') },
    { to: '/admin/parametres', icon: Settings, label: t('admin.nav.settings') },
  ];
  return (
    <div className="dashboard-shell">
      <aside className="sidebar w-64 hidden md:flex flex-col p-5 sticky top-0 h-screen">
        <LogoLight />
        <div className="text-xs text-white/50 mt-1 uppercase tracking-widest">{t('admin.space')}</div>
        <nav className="mt-8 flex-1 space-y-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${isActive ? 'active' : 'hover:bg-white/10'}`} data-testid={`nav-admin-${it.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <it.icon className="w-4 h-4"/> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
          <LanguageSwitcher variant="sidebar" />
          <ThemeToggle variant="sidebar"/>
        </div>
        <button onClick={logout} className="mt-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10 text-white/80" data-testid="btn-admin-logout">
          <LogOut className="w-4 h-4"/> {t('common.logout')}
        </button>
      </aside>
      <main className="flex-1 min-h-screen p-6 md:p-8 max-w-[1500px] w-full mx-auto">
        <div className="md:hidden mb-4 overflow-x-auto -mx-2 px-2 flex gap-2">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className={({isActive}) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border ${isActive ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700'}`}>
              {it.label}
            </NavLink>
          ))}
          <ThemeToggle variant="pill" />
          <button onClick={logout} className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs border bg-white text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700">{t('common.logout')}</button>
        </div>
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="clients/:id" element={<AdminClientDetail />} />
          <Route path="cartes" element={<AdminCards />} />
          <Route path="virements" element={<AdminTransfers />} />
          <Route path="virements/:id" element={<AdminTransferDetail />} />
          <Route path="transactions" element={<AdminTransactions />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="tickets" element={<AdminTickets />} />
          <Route path="chatbot" element={<AdminChatbotModeration />} />
          <Route path="exports" element={<AdminExports />} />
          <Route path="audit" element={<AdminAuditLog />} />
          <Route path="securite" element={<AdminSecurity />} />
          <Route path="parametres" element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
}
